import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  DeleteReminderItemDocument,
  GetReminderItemsDocument,
  ReminderItemDataFragment,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

type ReminderDeletePromptProps = {
  reminderItem: ReminderItemDataFragment;
  onDeleted?: (value: ReminderItemDataFragment) => void;
  onClose?: () => void;
};

export function ReminderDeletePrompt({
  reminderItem,
  onDeleted,
  onClose,
}: ReminderDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [deleteReminderItem] = useMutation(DeleteReminderItemDocument, {
    refetchQueries: [GetReminderItemsDocument],
  });

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deleteReminderItem({
        variables: {
          id: reminderItem?.id ?? '',
        },
      });

      if (result.data?.reminderDelete) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'patient.reminder-delete.success',
          }),
        });

        onDeleted?.(reminderItem);

        onClose?.();
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    reminderItem,
    deleteReminderItem,
    showAlertMessage,
    onDeleted,
    onClose,
  ]);

  if (!reminderItem) return null;

  return (
    <ModalPrompt
      testId="reminder-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="patient.reminder-delete.content" />
      </p>
    </ModalPrompt>
  );
}
